import React from 'react';

interface ITranscript {
  speaker: string;
  text: string;
}

interface ITranscripts {
  data: ITranscript[];
}

export const Transcripts: React.FC<ITranscripts> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-16 px-4'>
        <div className='w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4'>
          <svg
              className='w-10 h-10 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-semibold text-gray-600 mb-2'>
          No Transcript Available
        </h3>
        <p className='text-sm text-gray-500 text-center max-w-md'>
          The interview transcript will appear here once the recording is
          processed.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6 p-4 w-full'>
      { data.map((entry, index) => {
        const isPanelist =
          entry.speaker.toLowerCase().includes('panelist') ||
          entry.speaker.toLowerCase().includes('penalist');

        // Get initials from speaker name
        const getInitials = (name: string) => {
          const words = name.split(' ');
          if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
          }
          return name.substring(0, 2).toUpperCase();
        };

        const initials = getInitials(entry.speaker);

        return (
          <div
              key={index}
              className={`flex w-full gap-3 ${
                isPanelist ? 'justify-start' : 'justify-end'
              }`}
          >
            { /* Avatar for panelist (left side) */ }
            { isPanelist && (
              <div className='flex-shrink-0'>
                <div className='w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md'>
                  <span className='text-white text-sm font-semibold'>
                    { initials }
                  </span>
                </div>
              </div>
            ) }

            { /* Message bubble */ }
            <div
                className={`group max-w-[75%] ${
                  isPanelist ? '' : 'flex flex-col items-end'
                }`}
            >
              { /* Speaker name */ }
              <div
                  className={`mb-1.5 px-1 ${
                    isPanelist ? 'text-left' : 'text-right'
                  }`}
              >
                <span className='text-xs font-semibold text-gray-600 flex items-center gap-1.5'>
                  { isPanelist && (
                    <svg
                        className='w-3.5 h-3.5 text-blue-500'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                    >
                      <path
                          fillRule='evenodd'
                          d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                          clipRule='evenodd'
                      />
                    </svg>
                  ) }
                  { entry.speaker }
                  { !isPanelist && (
                    <svg
                        className='w-3.5 h-3.5 text-green-500'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                    >
                      <path
                          fillRule='evenodd'
                          d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                          clipRule='evenodd'
                      />
                    </svg>
                  ) }
                </span>
              </div>

              { /* Message content */ }
              <div
                  className={`relative p-4 rounded-2xl transition-all duration-200 ${
                    isPanelist
                      ? 'bg-gradient-to-br from-blue-50 to-indigo-50 text-gray-800 rounded-tl-sm border border-blue-100 hover:shadow-md'
                      : 'bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-tr-sm shadow-lg hover:shadow-xl'
                  }`}
              >
                { /* Quote decoration for panelist */ }
                { isPanelist && (
                  <div className='absolute -top-2 -left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-md'>
                    <svg
                        className='w-3 h-3 text-white'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                    >
                      <path d='M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z' />
                    </svg>
                  </div>
                ) }

                <p
                    className={`text-sm leading-relaxed ${
                      isPanelist ? 'text-gray-800' : 'text-white'
                    }`}
                >
                  { entry.text }
                </p>

                { /* Timestamp indicator (optional visual element) */ }
                <div
                    className={`mt-2 text-xs ${
                      isPanelist ? 'text-blue-400' : 'text-green-100'
                    } flex items-center gap-1`}
                >
                  <svg
                      className='w-3 h-3'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <span>Segment { index + 1 }</span>
                </div>
              </div>
            </div>

            { /* Avatar for candidate (right side) */ }
            { !isPanelist && (
              <div className='flex-shrink-0'>
                <div className='w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg'>
                  <span className='text-white text-sm font-semibold'>
                    { initials }
                  </span>
                </div>
              </div>
            ) }
          </div>
        );
      }) }
    </div>
  );
};
